import { Box, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'
import { Title } from '../../Title'
import styles from './BuyerHome.module.scss'
import instance from '../../../../../../../providers/axios.config'
import Notiflix from 'notiflix'
import { IImage, typeResponse } from '../../../../../../../utils/types'
import { CustomPagination } from '../../../../../../shared-components/pagination/CustomPagination'
import { useCustomNavigation } from '../../../../../../../hooks/useCustomNavigate'
import { NavigationEnum } from '../../../../../../../utils/constants'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import CloseIcon from '@mui/icons-material/Close'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import HistoryIcon from '@mui/icons-material/History'

interface IResponseImages extends typeResponse {
  data: {
    list: IImage[]
    pagination: {
      currentPage: number
      perPage: number
      totalPages: number
    }
  }
}

// Compact Image Card Component
interface ICompactImageCardProps {
  image: IImage
  onDelete: (imageId: number | string) => void
}

const CompactImageCard = ({ image, onDelete }: ICompactImageCardProps) => {
  const { t } = useTranslation()
  const customNavigate = useCustomNavigation()
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const handleNavigate = () => {
    const path = generatePath(NavigationEnum.ASSET_DETAILS, {
      assetName: `${image.title?.replaceAll(' ', '-')}-${image.id}` as string,
    })
    customNavigate(path as NavigationEnum, {
      imageId: image.id,
    })
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(image.id || '')
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  return (
    <div className={styles.compactCard} onClick={handleNavigate}>
      <div className={styles.imageWrapper}>
        {imageError ? (
          <div className={styles.imagePlaceholder}>
            <ImageNotSupportedIcon />
          </div>
        ) : (
          <>
            {!imageLoaded && <div className={styles.imageLoading} />}
            <LazyLoadImage
              alt={image.title}
              src={image.mediumURL || image.thumbURL}
              effect="opacity"
              onError={() => setImageError(true)}
              onLoad={handleImageLoad}
              wrapperClassName={styles.lazyWrapper}
            />
          </>
        )}
        
        {/* Hover Overlay */}
        <div className={styles.overlay}>
          {/* Delete Button */}
          <Tooltip title={t('remove_from_history', 'Remove from history')} arrow placement="top">
            <IconButton
              className={styles.deleteBtn}
              onClick={handleDelete}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>

          {/* Favorite Indicator */}
          {image.isFavorite && (
            <div className={styles.favoriteIndicator}>
              <FavoriteIcon />
            </div>
          )}

          {/* Title on hover */}
          <div className={styles.titleOverlay}>
            <span className={styles.imageTitle}>{image.title}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Skeleton for loading state
const CompactCardSkeleton = ({ isPortrait = false }: { isPortrait?: boolean }) => (
  <div className={`${styles.compactCard} ${styles.skeleton} ${isPortrait ? styles.portrait : ''}`}>
    <div className={styles.skeletonImage} />
  </div>
)

export const BuyerHome = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [isLoading, setIsLoading] = useState(false)
  const [visitedImages, setVisitedImages] = useState<IImage[]>([])
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 30,
    totalPages: 1
  })

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination({ ...pagination, currentPage: value })
  }

  const getMyImages = async () => {
    setIsLoading(true)
    try {
      const res = await instance.get(
        `history?limit=${pagination.perPage}&page=${pagination.currentPage}`
      ) as IResponseImages
      setVisitedImages(res?.data?.list || [])
      if (res?.data?.pagination && res?.data?.pagination.perPage) {
        setPagination({ ...res?.data?.pagination })
      }
    } catch (error) {
      Notiflix.Report.failure(
        'Error',
        `${t('error_api')}`,
        'OK'
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getMyImages()
  }, [pagination.currentPage])

  const handleDeleteImage = async (imageId: number | string) => {
    try {
      Notiflix.Loading.standard({ svgColor: '#c11b1e' })
      const resp = await instance.delete(`history/${imageId}`)
      if ((resp as any).status === 'success') {
        const filteredImages = visitedImages.filter((image) => image.id !== imageId)
        setVisitedImages([...filteredImages])
        Notiflix.Notify.success(t('Success'))
      }
    } catch (error) {
      console.error('Error delete files:', error)
      Notiflix.Notify.failure(t('error_delete', 'Failed to delete'))
    } finally {
      Notiflix.Loading.remove()
    }
  }

  // Empty state
  if (!isLoading && !visitedImages?.length) {
    return (
      <div className={styles.container}>
        <div className={styles.top}>
          <Title title={t('Home')} />
        </div>
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <HistoryIcon className={styles.emptyIcon} />
            <h3>{t('no_visited_images_title', 'No visited images yet')}</h3>
            <p>{t('no_images_visited', 'Images you view will appear here for easy access.')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <Title title={t('Home')} />
      </div>

      <div className={styles.content}>
        <div className={styles.sectionHeader}>
          <div className={styles.titleRow}>
            <HistoryIcon className={styles.titleIcon} />
            <h2 className={styles.title}>{t('Visited Images')}</h2>
          </div>
          <span className={styles.imageCount}>
            {visitedImages.length} {t('images', 'images')}
          </span>
        </div>

        {/* Flexible Row-Based Grid */}
        <div className={styles.flexGrid}>
          {isLoading ? (
            // Loading skeletons - mix of orientations
            Array.from({ length: 20 }).map((_, index) => (
              <CompactCardSkeleton key={index} isPortrait={index % 5 === 0} />
            ))
          ) : (
            // Actual images
            visitedImages.map((image) => (
              <CompactImageCard
                key={image.id}
                image={image}
                onDelete={handleDeleteImage}
              />
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination?.totalPages > 1 && (
          <div className={styles.paginationWrapper}>
            <CustomPagination
              shape="rounded"
              count={pagination?.totalPages}
              page={pagination?.currentPage}
              onChange={handleChange}
              size={isMobile ? 'medium' : 'large'}
              className={styles.pagination}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default BuyerHome